import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { LuSearch, LuX } from "react-icons/lu";
import { toast } from "react-toastify";
import {
  assignLogisticsRfqUser,
  extractApiMessage,
  fetchLogisticsRfqUserAssignees,
} from "../../services/logisticsRfqUserAssignment.service";
import { hasAssignedLogisticsUser } from "./LogisticsRfqAssignedUserBadge";
import "../../assets/styles/super_admin_access.scss";
import "../../assets/styles/business_relocation_rfq_provider_assign.scss";

export default function LogisticsUserAssignModal({
  open,
  rfq,
  rfqKind = "business",
  onClose,
  onAssigned,
}) {
  const [assignees, setAssignees] = useState([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [search, setSearch] = useState("");
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isReassignment = hasAssignedLogisticsUser(rfq);
  const rfqLabel = rfq?.rfqReference || rfq?.rfqId || "RFQ";

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setAssigneeId("");
    setSearch("");
    setLoadingAssignees(true);
    fetchLogisticsRfqUserAssignees(rfqKind)
      .then((items) => {
        if (!cancelled) setAssignees(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(extractApiMessage(err, "Could not load logistics users."));
          setAssignees([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAssignees(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, rfqKind]);

  useEffect(() => {
    if (!open || !rfq || loadingAssignees || assignees.length === 0) return;
    const currentId = rfq.assignedLogisticsUserId ?? rfq.assignedLogisticsUserAccessUserId;
    if (currentId == null) return;
    const match = assignees.find((user) => String(user.id) === String(currentId));
    if (match) setAssigneeId(String(match.id));
  }, [open, rfq, assignees, loadingAssignees]);

  const filteredAssignees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return assignees;
    return assignees.filter((user) => {
      const name = String(user.name || "").toLowerCase();
      const email = String(user.email || "").toLowerCase();
      const role = String(user.roleLabel || "").toLowerCase();
      return name.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [assignees, search]);

  if (!open || !rfq) return null;

  const submit = async () => {
    if (!assigneeId) {
      toast.error("Select a Logistics Manager or Logistics User.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await assignLogisticsRfqUser(rfqKind, rfq.id, assigneeId);
      const reassigned = Boolean(result?.wasReassignment ?? isReassignment);
      toast.success(
        reassigned
          ? "Logistics User reassigned successfully."
          : "Logistics User assigned successfully.",
      );
      onAssigned?.(result, rfq.id);
      onClose?.();
    } catch (err) {
      toast.error(extractApiMessage(err, "Could not assign Logistics User."));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="sa-dispute-modal-overlay brpq-assign-overlay" role="presentation" onClick={onClose}>
      <div
        className="sa-dispute-modal brpq-assign-modal lbru-user-assign-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="logistics-user-assign-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="sa-dispute-modal__header">
          <div>
            <h2 id="logistics-user-assign-title">
              {isReassignment ? "Reassign Logistics User" : "Assign Logistics User"}
            </h2>
            <p>
              {rfqLabel} · Select a Logistics Manager or Logistics User from your team.
            </p>
          </div>
          <button type="button" className="sa-dispute-modal__close" onClick={onClose} aria-label="Close">
            <LuX aria-hidden />
          </button>
        </header>

        <div className="sa-dispute-modal__body brpq-assign-modal__body">
          <label className="sa-access-search brpq-assign-search">
            <LuSearch aria-hidden />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or email"
              disabled={loadingAssignees || submitting}
              autoFocus
            />
          </label>

          <div className="sa-access-table-wrap brpq-assign-table-wrap lbru-user-assign-table-wrap">
            <table className="sa-access-table brpq-assign-table lbru-user-assign-table">
              <thead>
                <tr>
                  <th aria-label="Select" />
                  <th>Full Name</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Assigned RFQs</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loadingAssignees ? (
                  <tr>
                    <td colSpan={6} className="brpq-assign-empty">
                      Loading assignees…
                    </td>
                  </tr>
                ) : filteredAssignees.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="brpq-assign-empty">
                      No Logistics Managers or Users match your search.
                    </td>
                  </tr>
                ) : (
                  filteredAssignees.map((user) => {
                    const selected = String(assigneeId) === String(user.id);
                    const busy = user.availabilityStatus === "Busy";
                    return (
                      <tr
                        key={user.id}
                        className={selected ? "is-selected" : ""}
                        onClick={() => !submitting && setAssigneeId(String(user.id))}
                      >
                        <td>
                          <input
                            type="radio"
                            name="logistics-user-assignee"
                            checked={selected}
                            onChange={() => setAssigneeId(String(user.id))}
                            disabled={submitting}
                            aria-label={`Select ${user.name}`}
                          />
                        </td>
                        <td>{user.name}</td>
                        <td>
                          <span
                            className={`brrq-admin-role-badge${
                              user.role === "logistics_manager"
                                ? " brrq-admin-role-badge--manager"
                                : ""
                            }`}
                          >
                            {user.roleLabel || "Logistics User"}
                          </span>
                        </td>
                        <td>{user.email || "—"}</td>
                        <td>{Number(user.assignedRfqCount ?? 0)}</td>
                        <td>
                          <span
                            className={`brrq-admin-availability-badge${
                              busy ? " brrq-admin-availability-badge--busy" : ""
                            }`}
                          >
                            {user.availabilityStatus || "Available"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="brpq-assign-footer">
          <button
            type="button"
            className="sa-access-btn sa-access-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sa-access-btn sa-access-btn--primary brrq-admin-assign-btn--orange"
            onClick={() => void submit()}
            disabled={submitting || loadingAssignees || !assigneeId}
          >
            {submitting
              ? isReassignment
                ? "Reassigning…"
                : "Assigning…"
              : isReassignment
                ? "Reassign Logistics User"
                : "Assign Logistics User"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
